import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  LayoutDashboard, 
  Users, 
  ClipboardCheck, 
  Shield,
  ChevronDown,
  ChevronUp,
  UserCog,
  FileSpreadsheet,
  FileDown,
  FileText,
  Save,
  Send,
  Upload,
  Download,
  HelpCircle,
  CheckCircle2,
  AlertCircle,
  Info
} from 'lucide-react';

const GuidaRapida = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  const [expandedSections, setExpandedSections] = useState({
    dashboard: true,
    compilazione: !isAdmin,
    salvataggio: false,
    export: false,
    utenti: false,
    questionari: false,
    ruoli: false,
    supporto: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const Section = ({ id, icon: Icon, title, children, color = '#3BA99C' }) => (
    <Card className="overflow-hidden">
      <CardHeader 
        className="cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => toggleSection(id)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3 text-lg">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${color}20` }}
            >
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            {title}
          </CardTitle>
          {expandedSections[id] ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </CardHeader>
      {expandedSections[id] && (
        <CardContent className="pt-0 animate-in slide-in-from-top-2 duration-200">
          {children}
        </CardContent>
      )}
    </Card>
  );

  const InfoCard = ({ title, items, color = '#3BA99C' }) => (
    <div 
      className="p-4 rounded-lg border-l-4"
      style={{ borderLeftColor: color, backgroundColor: `${color}08` }}
    >
      <h4 className="font-semibold text-slate-900 mb-2">{title}</h4>
      <ul className="space-y-1">
        {items.map((item, idx) => (
          <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: color }} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );

  const StepCard = ({ number, title, description, icon: Icon, color = '#3BA99C' }) => (
    <div className="flex gap-4 p-4 bg-slate-50 rounded-lg">
      <div 
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold"
        style={{ backgroundColor: color }}
      >
        {number}
      </div>
      <div>
        <h4 className="font-semibold text-slate-900 flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4" style={{ color }} />}
          {title}
        </h4>
        <p className="text-sm text-slate-600 mt-1">{description}</p>
      </div>
    </div>
  );

  const RoleCard = ({ icon: Icon, title, permissions, color }) => (
    <div className="p-4 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors">
      <div className="flex items-center gap-3 mb-3">
        <div 
          className="w-12 h-12 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <h4 className="font-bold text-slate-900">{title}</h4>
      </div>
      <ul className="space-y-2">
        {permissions.map((perm, idx) => (
          <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: color }} />
            {perm}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-2 py-6">
        <h1 className="text-4xl font-bold text-slate-900" data-testid="guida-title">
          {isAdmin ? 'Guida Amministratore e-CAA' : 'Guida Utente e-CAA'}
        </h1>
        <p className="text-slate-500 text-lg">
          {isAdmin 
            ? 'Guida completa per la gestione del sistema e-CAA'
            : 'Guida alla compilazione dei questionari e utilizzo della piattaforma'
          }
        </p>
      </div>

      {/* ========== SEZIONI PER TUTTI GLI UTENTI ========== */}
      
      {/* Section: Compilazione Questionari */}
      <Section id="compilazione" icon={ClipboardCheck} title="Compilazione Questionari" color="#3BA99C">
        <p className="text-slate-600 mb-4">
          La compilazione del questionario è il cuore della piattaforma e-CAA. Ecco come procedere passo dopo passo.
        </p>
        
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
              <Info className="w-5 h-5" />
              Prima di iniziare
            </h4>
            <p className="text-sm text-blue-700">
              Il questionario è composto da <strong>30 domande</strong> suddivise in <strong>6 aree tematiche</strong>: 
              Personale, Terze Parti, Misure Organizzative, Sicurezza Fisica, ICT e Incidenti di Sicurezza.
              Ogni risposta ha un punteggio da 0 a 4 che rappresenta il livello di maturità.
            </p>
          </div>

          <h4 className="font-semibold text-slate-900 mt-6 mb-3">Come compilare il questionario</h4>
          
          <div className="space-y-3">
            <StepCard 
              number="1"
              title="Accedi ai Questionari"
              description="Dal menu laterale, clicca su 'Questionari'. Vedrai l'elenco dei tuoi questionari esistenti o potrai crearne uno nuovo cliccando su 'Nuovo Questionario'."
              color="#3BA99C"
            />
            <StepCard 
              number="2"
              title="Seleziona il livello di maturità"
              description="Per ogni domanda, seleziona il livello che meglio descrive la situazione attuale della tua organizzazione (da 0 - Non implementato a 4 - Eccellente)."
              color="#3BA99C"
            />
            <StepCard 
              number="3"
              title="Compila le procedure (obbligatorie per livello ≥ 2)"
              description="Se selezioni un livello 2, 3 o 4, devi obbligatoriamente descrivere le procedure/documenti che supportano la risposta. Questo campo è fondamentale per la valutazione."
              icon={FileText}
              color="#E57D68"
            />
            <StepCard 
              number="4"
              title="Naviga tra le aree"
              description="Usa i tab colorati in alto per passare da un'area all'altra. Ogni tab mostra il progresso (es. 5/5 domande completate)."
              color="#3BA99C"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <InfoCard 
              title="Scala di Maturità"
              color="#3BA99C"
              items={[
                "0 - Non implementato (0%): Processo non gestito",
                "1 - Carente (25%): Gestione informale, non documentata",
                "2 - Accettabile (55%): Processo formalizzato e documentato",
                "3 - Buono (80%): Monitoraggio con KPI e metriche",
                "4 - Eccellente (100%): Miglioramento continuo integrato"
              ]}
            />
            <InfoCard 
              title="Suggerimenti"
              color="#5941F5"
              items={[
                "Leggi attentamente ogni domanda prima di rispondere",
                "Consulta la documentazione interna per le procedure",
                "In caso di dubbio, scegli il livello che meglio rappresenta la realtà attuale",
                "Non sovrastimare: le risposte devono essere supportate da evidenze"
              ]}
            />
          </div>
        </div>
      </Section>

      {/* Section: Salvataggio e Invio */}
      <Section id="salvataggio" icon={Save} title="Salvataggio e Invio" color="#5941F5">
        <p className="text-slate-600 mb-4">
          Il sistema offre diverse opzioni per salvare il tuo lavoro e inviare il questionario completato.
        </p>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                <Save className="w-5 h-5" />
                Salva in Bozza
              </h4>
              <ul className="text-sm text-amber-700 space-y-2">
                <li>• Clicca il pulsante <strong>"Salva Bozza"</strong> per salvare il lavoro in corso</li>
                <li>• Puoi interrompere e riprendere la compilazione in qualsiasi momento</li>
                <li>• Le bozze sono visibili nella lista questionari con stato "In Bozza"</li>
                <li>• Puoi modificare una bozza tutte le volte che vuoi</li>
              </ul>
            </div>
            
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Importante
              </h4>
              <p className="text-sm text-slate-600">
                Il salvataggio automatico <strong>non è attivo</strong>. Ricordati di salvare manualmente 
                il tuo lavoro prima di chiudere la pagina per non perdere le modifiche.
              </p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-2">
                <Send className="w-5 h-5" />
                Invia Questionario
              </h4>
              <ul className="text-sm text-green-700 space-y-2">
                <li>• Il pulsante <strong>"Invia"</strong> appare solo quando tutte le domande sono complete</li>
                <li>• Tutte le 30 domande devono avere una risposta</li>
                <li>• Tutte le procedure obbligatorie devono essere compilate</li>
                <li>• <strong>Dopo l'invio il questionario non sarà più modificabile</strong></li>
              </ul>
            </div>
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5" />
                Stato Completato
              </h4>
              <p className="text-sm text-blue-700">
                Una volta inviato, il questionario passa allo stato "Completato" e potrai:
                visualizzare i risultati, scaricare il report PDF completo ed esportare i dati.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Section: Import/Export */}
      <Section id="export" icon={FileSpreadsheet} title="Import ed Export Dati" color="#21358E">
        <p className="text-slate-600 mb-4">
          La piattaforma offre diverse funzionalità per importare ed esportare i dati del questionario.
        </p>
        
        <div className="space-y-6">
          {/* Export Section */}
          <div>
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Download className="w-5 h-5 text-[#21358E]" />
              Funzioni di Export
            </h4>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-5 h-5 text-amber-600" />
                  <h5 className="font-semibold text-amber-800">Procedure</h5>
                </div>
                <p className="text-sm text-amber-700">
                  Esporta solo l'elenco delle procedure e dei documenti citati nelle risposte. 
                  Utile per avere un riepilogo della documentazione di riferimento.
                </p>
              </div>
              
              <div className="p-4 border border-emerald-200 bg-emerald-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                  <h5 className="font-semibold text-emerald-800">Esporta Excel</h5>
                </div>
                <p className="text-sm text-emerald-700">
                  Scarica tutte le risposte del questionario in formato Excel (.xlsx). 
                  Include domande, risposte, livelli e procedure per ogni voce.
                </p>
              </div>
              
              <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FileDown className="w-5 h-5 text-blue-600" />
                  <h5 className="font-semibold text-blue-800">Report PDF</h5>
                </div>
                <p className="text-sm text-blue-700">
                  Genera un report PDF completo con grafici, punteggi per area e tutte le risposte dettagliate. 
                  Disponibile solo per questionari completati.
                </p>
              </div>
            </div>
          </div>
          
          {/* Import Section */}
          <div>
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#21358E]" />
              Funzione di Import
            </h4>
            
            <div className="p-4 border border-slate-200 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Upload className="w-5 h-5 text-blue-600" />
                <h5 className="font-semibold text-slate-800">Importa da Excel</h5>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Puoi compilare il questionario offline usando un file Excel e poi importare le risposte.
                Questo è utile se vuoi preparare le risposte con calma o coinvolgere colleghi nella compilazione.
              </p>
              
              <div className="bg-white p-3 rounded border border-slate-200">
                <h6 className="font-medium text-slate-800 mb-2">Formato del file:</h6>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• <strong>N.</strong> - Numero della domanda (1-30)</li>
                  <li>• <strong>Risposta</strong> - Valore numerico da 0 a 4</li>
                  <li>• <strong>Procedure</strong> - Testo delle procedure (opzionale)</li>
                </ul>
                <p className="text-xs text-slate-500 mt-2 italic">
                  💡 Suggerimento: usa prima "Esporta" per ottenere un file template già formattato correttamente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ========== SEZIONI SOLO PER ADMIN ========== */}
      {isAdmin && (
        <>
          {/* Section: Dashboard Admin */}
          <Section id="dashboard" icon={LayoutDashboard} title="Dashboard Amministratore" color="#3BA99C">
            <p className="text-slate-600 mb-4">
              La Dashboard Amministratore fornisce una visione d'insieme completa del sistema e-CAA.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <InfoCard 
                title="Statistiche Aggregate"
                color="#3BA99C"
                items={[
                  "Numero totale di Società di servizi registrate",
                  "Conteggio utenti attivi nel sistema",
                  "Totale questionari compilati",
                  "Media generale dei punteggi"
                ]}
              />
              <InfoCard 
                title="Panoramica Società di servizi"
                color="#60D1B4"
                items={[
                  "Visualizza l'elenco delle Società di servizi",
                  "Vedi i questionari più recenti",
                  "Clicca su una riga per accedere ai dettagli",
                  "Accedi ai questionari della Società di servizi"
                ]}
              />
            </div>
          </Section>

          {/* Section: Gestione Account */}
          <Section id="utenti" icon={Users} title="Gestione Account" color="#5941F5">
            <p className="text-slate-600 mb-4">
              La sezione Gestione Account permette di amministrare tutti gli account del sistema e, indirettamente, le Società di servizi e i loro uffici.
            </p>
            
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                  <Info className="w-5 h-5" />
                  Come funziona
                </h4>
                <p className="text-sm text-blue-700">
                  Le <strong>Società di servizi</strong> e i loro <strong>Uffici CAA</strong> vengono creati automaticamente quando 
                  aggiungi un nuovo utente. Se specifichi una Società o un Ufficio che non esiste, il sistema lo creerà per te.
                </p>
              </div>
              
              <InfoCard 
                title="Creazione Utente Singolo"
                color="#5941F5"
                items={[
                  "Clicca 'Nuovo Utente' per creare un account",
                  "Specifica email, nome completo e ruolo (Admin o Utente)",
                  "Per gli utenti non-admin: seleziona o crea una nuova Società di servizi",
                  "Specifica il codice e nome dell'Ufficio CAA di appartenenza",
                  "L'utente riceverà un'email con le istruzioni per impostare la password"
                ]}
              />
              <InfoCard 
                title="Importazione Massiva da Excel"
                color="#21358E"
                items={[
                  "Usa 'Importa Utenti' per caricare un file Excel con più utenti",
                  "Il file deve contenere: Email, Nome, Ruolo, Società di servizi, Codice Ufficio, Nome Ufficio",
                  "Il sistema creerà automaticamente Società e Uffici se non esistono",
                  "Scarica il template Excel per vedere il formato richiesto"
                ]}
              />
              <InfoCard 
                title="Gestione Utenti Esistenti"
                color="#89E3FC"
                items={[
                  "Visualizza l'elenco completo degli utenti con filtri per Società e Ufficio",
                  "Modifica i dati di un utente cliccando sull'icona 'matita'",
                  "Reimposta la password cliccando sull'icona 'chiave' (l'utente riceverà un'email)",
                  "Esporta l'elenco utenti in formato Excel"
                ]}
              />
            </div>
          </Section>

          {/* Section: Revisione Questionari */}
          <Section id="questionari" icon={ClipboardCheck} title="Questionari e Statistiche" color="#E57D68">
            <p className="text-slate-600 mb-4">
              Come amministratore, puoi visualizzare tutti i questionari e accedere a statistiche aggregate.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <InfoCard 
                title="Sezione Questionari"
                color="#E57D68"
                items={[
                  "Visualizza tutti i questionari di tutte le Società di servizi",
                  "Filtra per Società, Ufficio, stato (bozza/completato)",
                  "Accedi ai dettagli di ogni questionario",
                  "Visualizza e scarica i report PDF completi"
                ]}
              />
              <InfoCard 
                title="Sezione Statistiche"
                color="#21358E"
                items={[
                  "Confronta più questionari tra loro",
                  "Visualizza graduatorie e classifiche per punteggio",
                  "Analizza i punteggi medi per area tematica",
                  "Genera report PDF di confronto con grafici"
                ]}
              />
            </div>
          </Section>

          {/* Section: Ruoli */}
          <Section id="ruoli" icon={Shield} title="Ruoli Utente" color="#469D85">
            <p className="text-slate-600 mb-6">
              Il sistema e-CAA prevede due ruoli distinti con permessi differenziati:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <RoleCard 
                icon={UserCog}
                title="Amministratore"
                color="#3BA99C"
                permissions={[
                  "Accesso completo a tutte le funzionalità",
                  "Gestione utenti e Società di servizi",
                  "Visualizzazione di tutti i questionari",
                  "Gestione sedi e uffici CAA",
                  "Accesso alle statistiche aggregate",
                  "Backup e ripristino dati"
                ]}
              />
              <RoleCard 
                icon={Users}
                title="Utente"
                color="#5941F5"
                permissions={[
                  "Accesso limitato ai propri uffici/sedi",
                  "Compilazione e gestione dei propri questionari",
                  "Visualizzazione dei propri risultati",
                  "Export dati dei propri questionari",
                  "Visualizzazione della propria Dashboard"
                ]}
              />
            </div>
          </Section>
        </>
      )}

      {/* Section: Supporto (per tutti) */}
      <Section id="supporto" icon={HelpCircle} title="Supporto e Assistenza" color="#E57D68">
        <div className="p-4 bg-slate-50 rounded-lg">
          <p className="text-slate-600 mb-4">
            Per qualsiasi domanda o problema tecnico, puoi contattare il supporto:
          </p>
          <div className="flex items-center gap-3 p-3 bg-white rounded border border-slate-200">
            <div className="w-10 h-10 rounded-full bg-[#E57D68] flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">Help Desk e-CAA</p>
              <a 
                href="mailto:hd@etom.it" 
                className="text-[#3BA99C] hover:underline"
              >
                hd@etom.it
              </a>
            </div>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <div className="text-center py-6 text-sm text-slate-400">
        <p>Guida {isAdmin ? 'Amministratore' : 'Utente'} e-CAA • Versione 2.0</p>
      </div>
    </div>
  );
};

export default GuidaRapida;
